import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, PanelLeftClose } from "lucide-react";

/* The day's patients, alongside the chart.
 *
 * Reception's job is a queue, not a lookup: see who is waiting, open them, chart
 * them, move to the next. Our patient page made that a round trip — back to the
 * list, find the next name, click in — so the list was the home screen and the
 * chart was a detour. Practo keeps the queue beside the chart, and the whole
 * difference is not having to leave.
 *
 * DUE vs MET is the split that matters: DUE is anyone who has not been seen
 * yet, MET is anyone the doctor has finished with. Cancellations and no-shows
 * are excluded entirely — they are not in the queue and never will be.
 */

const MET_STATUSES = new Set(["checked_out", "completed", "visited"]);
const GONE_STATUSES = new Set(["cancelled", "no_show"]);

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const h = d.getHours();
  return `${h % 12 === 0 ? 12 : h % 12}:${String(d.getMinutes()).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

// How long ago a patient was seen. Ray puts this on the MET rows, and it is the
// number reception is actually asked for — "how long has Mrs Shah been done"
// decides who gets chased for payment and who is still with the doctor.
function elapsed(iso) {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 0 || Number.isNaN(mins)) return "";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"}`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} hour${h === 1 ? "" : "s"} ${m} minute${m === 1 ? "" : "s"}`
           : `${h} hour${h === 1 ? "" : "s"}`;
}

// A-Z index for the All tab. Ray groups by the literal first character, which
// is why their list opens on "8", "9", "+" — a patient base captured by phone
// number has plenty of names that are not names.
function groupAlphabetically(rows) {
  const groups = new Map();
  rows.forEach((p) => {
    const ch = (p.name || "").trim().charAt(0).toUpperCase() || "#";
    const key = /[A-Z0-9]/.test(ch) ? ch : "+";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(p);
  });
  // Sort WITHIN each group too. The API returns newest-first, so grouping alone
  // gave an "A" section reading Aditya, Anjali, Arjun, Amit, Aarti — which
  // looks alphabetical at a glance and then isn't, the worst of both.
  groups.forEach((list) => list.sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })));
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
}

const doctorName = (id, doctors) => doctors.find((d) => d.id === id)?.name || "";

export default function PatientQueueRail({ currentId, onClose, refreshKey = 0 }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("today");
  const [appts, setAppts] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");

  // refreshKey: the page bumps it after editing a patient, so a new name shows here.
  useEffect(() => {
    api.get("/appointments").then((r) => setAppts(r.data || [])).catch(() => {});
  }, [refreshKey]);
  useEffect(() => {
    api.get("/users", { params: { role: "doctor" } })
      .then((r) => setDoctors(r.data || [])).catch(() => {});
  }, []);

  const loadPatients = useCallback(() => {
    // Recent is the last handful touched; All is the whole book, indexed A-Z,
    // so it needs a much bigger page or the index is a lie.
    const params = { limit: tab === "all" ? 200 : 40 };
    if (tab === "all" && search.trim()) params.search = search.trim();
    api.get("/patients", { params }).then((r) => setPatients(r.data.items || [])).catch(() => {});
  }, [tab, search]);
  useEffect(() => { if (tab !== "today") loadPatients(); }, [tab, loadPatients, refreshKey]);

  // All is an alphabetical index; Recent stays in recency order.
  const alphabetical = useMemo(
    () => (tab === "all" ? groupAlphabetically(patients) : []),
    [tab, patients],
  );

  const { due, met } = useMemo(() => {
    const day = todayISO();
    const rows = appts
      .filter((a) => (a.scheduled_at || "").slice(0, 10) === day && !GONE_STATUSES.has(a.status))
      .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""));
    return {
      due: rows.filter((a) => !MET_STATUSES.has(a.status)),
      met: rows.filter((a) => MET_STATUSES.has(a.status)),
    };
  }, [appts]);

  const go = (patientId) => { if (patientId) navigate(`/patients/${patientId}`); };

  // One row shape for the whole rail. `since` is the elapsed-time note Ray shows
  // on patients who have already been seen.
  const Row = ({ id, title, sub, doctor, time, since, active }) => (
    <button onClick={() => go(id)} disabled={!id}
      data-testid="queue-row"
      className={`flex w-full gap-2 border-b border-navy/5 px-3 py-2 text-left transition-colors last:border-b-0 ${
        active ? "bg-navy text-white" : "hover:bg-nude/40"} ${!id ? "cursor-default opacity-60" : ""}`}>
      {time && (
        <span className={`w-12 shrink-0 pt-0.5 text-[11px] font-semibold ${
          active ? "text-white/70" : "text-muted-foreground"}`}>{time}</span>
      )}
      <span className="min-w-0 flex-1">
        <span className={`block truncate text-sm font-medium ${active ? "text-white" : "text-ink"}`}>{title}</span>
        {sub && (
          <span className={`block truncate text-xs ${active ? "text-white/60" : "text-muted-foreground"}`}>{sub}</span>
        )}
        {doctor && (
          <span className={`block truncate text-xs ${active ? "text-white/60" : "text-muted-foreground"}`}>{doctor}</span>
        )}
      </span>
      {since && (
        <span className="flex shrink-0 items-center gap-1 pt-0.5 text-[10px] text-muted-foreground">
          {since}
          <span className="h-1.5 w-1.5 rounded-full bg-cyan-500" />
        </span>
      )}
    </button>
  );

  return (
    <div className="h-fit overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-navy/10 px-3 py-2">
        <p className="font-display text-sm font-semibold text-navy">Patients</p>
        <button onClick={onClose} title="Hide the queue"
                className="rounded p-1 text-muted-foreground hover:bg-nude hover:text-navy">
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>

      <div className="border-b border-navy/10 px-2 py-2">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full">
            <TabsTrigger value="today" className="flex-1 text-xs" data-testid="queue-tab-today">Today</TabsTrigger>
            <TabsTrigger value="recent" className="flex-1 text-xs">Recent</TabsTrigger>
            <TabsTrigger value="all" className="flex-1 text-xs">All</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === "all" && (
          <div className="relative mt-2">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input className="h-8 pl-7 text-xs" placeholder="Search patients…"
                   value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        )}
      </div>

      <div className="max-h-[34rem] overflow-y-auto">
        {tab === "today" ? (
          <>
            <SectionLabel>Due{due.length ? ` (${due.length})` : ""}</SectionLabel>
            {due.length === 0
              ? <Empty>Nobody left to see.</Empty>
              : due.map((a) => (
                  <Row key={a.id} id={a.patient_id} time={fmtTime(a.scheduled_at)}
                       title={a.patient_name}
                       sub={a.service || "Visit reason not specified"}
                       doctor={a.doctor_name || doctorName(a.doctor_id, doctors)}
                       active={a.patient_id === currentId} />
                ))}
            <SectionLabel>Met{met.length ? ` (${met.length})` : ""}</SectionLabel>
            {met.length === 0
              ? <Empty>Nobody seen yet.</Empty>
              : met.map((a) => (
                  <Row key={a.id} id={a.patient_id} time={fmtTime(a.scheduled_at)}
                       title={a.patient_name}
                       sub={a.service || "Visit reason not specified"}
                       doctor={a.doctor_name || doctorName(a.doctor_id, doctors)}
                       since={elapsed(a.checked_out_at || a.consultation_started_at)}
                       active={a.patient_id === currentId} />
                ))}
          </>
        ) : tab === "recent" ? (
          // Names only. Ray's Recent is a jump list, not a summary — anything
          // more makes it slower to scan for the one name you already know.
          patients.length === 0
            ? <Empty>No patients.</Empty>
            : patients.map((p) => (
                <Row key={p.id} id={p.id} title={p.name} active={p.id === currentId} />
              ))
        ) : alphabetical.length === 0 ? (
          <Empty>No patients.</Empty>
        ) : alphabetical.map(([letter, rows]) => (
          <div key={letter}>
            <SectionLabel>{letter}</SectionLabel>
            {rows.map((p) => (
              <Row key={p.id} id={p.id} title={p.name}
                   sub={p.patient_no} active={p.id === currentId} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

const SectionLabel = ({ children }) => (
  <p className="bg-cream/60 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
    {children}
  </p>
);

const Empty = ({ children }) => (
  <p className="px-3 py-4 text-center text-xs text-muted-foreground">{children}</p>
);
